import { Component, OnDestroy, OnInit } from '@angular/core';
import { OldSystemIframeComponent } from '../shared-components/old-system-iframe/old-system-iframe.component';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { UserService } from 'app/core/services/user.service';
import { HeadlineService } from 'app/core/services/headline.service';
import { SharedService } from 'app/core/services/shared.service';
import {
    firstValueFrom,
    forkJoin,
    interval,
    lastValueFrom,
    Subscription,
    take,
} from 'rxjs';
import { environment } from 'environments/environment';
import { HippoConfirmationService } from '@hippo/services/confirmation';
import {
    provideTranslocoScope,
    translate,
    TranslocoService,
} from '@jsverse/transloco';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { NoPageAccessComponent } from '../shared-components/no-page-access/no-page-access.component';

interface InspectorData {
    database: {
        hd: {
            name: string;
            status: string;
        };
        hd_base: {
            name: string;
            status: string;
        };
        hd_central: {
            name: string;
            status: string;
        };
        hd_store: {
            name: string;
            status: string;
        };
    };
    date: string;
    jobs: {
        invoice_check: {
            file_status: string;
        };
    };
    logs: {
        common: {
            file_status: string;
            key: string;
        };
        database: {
            hd: {
                file_status: string;
                key: string;
            };
            hd_central: {
                file_status: string;
                key: string;
            };
            hd_store: {
                file_status: string;
                key: string;
            };
        };
    };
    timestamp: number;
}

@Component({
    selector: 'app-inspector',
    imports: [
        OldSystemIframeComponent,
        CommonModule,
        MatCardModule,
        MatButtonModule,
        NoPageAccessComponent,
    ],
    templateUrl: './inspector.component.html',
    styleUrl: './inspector.component.scss',
    providers: [
        provideTranslocoScope('common'),
        provideTranslocoScope('codext'),
    ],
})
export class InspectorComponent implements OnInit, OnDestroy {
    data: InspectorData;
    sub: Subscription;
    environment = environment;
    date: Date = null;
    hasPageAccess: boolean = false;

    constructor(
        private httpClient: HttpClient,
        public userService: UserService,
        public headlineService: HeadlineService,
        private sharedService: SharedService,
        private hippoConfirmationService: HippoConfirmationService,
        private translocoService: TranslocoService
    ) {}

    async ngOnInit() {
        this.headlineService.setHeadline(['hippo Inspector'], 'inspector');
        await firstValueFrom(
            forkJoin([
                this.translocoService.selectTranslation('common').pipe(take(1)),
                this.translocoService.selectTranslation('codext').pipe(take(1)),
            ])
        );
        await this.getData();
        this.sub = interval(10000).subscribe(async () => {
            await this.getData();
        });
    }

    async getData() {
        try {
            const formData = new FormData();
            formData.append('key', 'check_hippo');
            if (this.date !== null) {
                let month = (this.date.getMonth() + 1).toString();
                if (month.length === 1) {
                    month = '0' + month;
                }
                let day = this.date.getDate().toString();
                if (day.length === 1) {
                    day = '0' + day;
                }
                let dateForApi =
                    this.date.getFullYear().toString() +
                    '-' +
                    month +
                    '-' +
                    day;
                formData.append('date', dateForApi);
            }
            const data = await lastValueFrom(
                this.httpClient.post<any>(
                    `hippo/ajax_navigator.php?page=pages/administration/Ajax_inspector_state.php&uid=${this.userService.user.uid}&page_type=json`,
                    formData,
                    {
                        observe: 'response',
                    }
                )
            );
            this.data = JSON.parse(JSON.stringify(data.body.result));
            this.hasPageAccess = true;
        } catch (error) {
            console.log(error);
            const result = error?.error?.result;

            if (
                result === 'page access denied' ||
                result?.message === 'page access denied'
            ) {
                this.hasPageAccess = false;
            } else {
                this.sharedService.showDataNotLoaded(
                    error,
                    'InspectorComponent',
                    'getData'
                );
            }
        }
    }

    getLogsImage(status: string): string {
        switch (status) {
            case 'ok':
                return 'green';
            case 'no_file':
                return 'green';
            case 'error':
                return 'red';
            case 'warn':
                return 'orange';

            default:
                return 'red';
        }
    }

    getInvoiceCheckImage(status: string): string {
        switch (status) {
            case 'missing':
                return 'red';
            case 'no_file':
                return 'red';
            case 'error':
                return 'red';
            case 'ok':
                return 'green';

            default:
                return 'red';
        }
    }

    async openLogFileWindow(folder: string) {
        try {
            let date = Number.isInteger(this.data.timestamp)
                ? this.sharedService.toUtcDateFromTimestamp(
                      new Date(this.data.timestamp * 1000)
                  )
                : new Date();
            // const data = await lastValueFrom(
            //     this.httpClient.post<any>(
            //         `hippo/navigator.php?page=pages/info/System_log_check.php&uid=${this.userService.user.uid}&page_type=json&f=${folder}&y=${date.getFullYear().toString()}&m=${('0' + (date.getMonth() + 1).toString()).slice(-2)}&d=${('0' + date.getDate().toString()).slice(-2)}&pwd=`,
            //         {
            //             observe: 'response',
            //         }
            //     )
            // );
            // console.log(data);
            window.open(
                `${environment.apiURL}navigator.php?page=pages/info/System_log_check.php&uid=${this.userService.user.uid}&f=${folder}&y=${date.getFullYear().toString()}&m=${('0' + (date.getMonth() + 1).toString()).slice(-2)}&d=${('0' + date.getDate().toString()).slice(-2)}&pwd=`,
                '_blank'
            );
        } catch (error) {
            console.log(error);
        }
    }

    requestInvoiceInformation() {
        const confirmation = this.hippoConfirmationService.open({
            title: '',
            message: translate('codext.wantStartInvoiceAudit'),
            icon: {
                show: true,
                color: 'warn',
                name: 'heroicons_outline:question-mark-circle',
            },
            actions: {
                confirm: {
                    label: translate('common.yes'),
                    color: 'accent',
                },
                cancel: {
                    show: true,
                    label: translate('common.no'),
                },
            },
            dismissible: false,
        });
        confirmation.afterClosed().subscribe(async result => {
            if (result === 'confirmed') {
                window.open(
                    `${environment.apiURL}cron_navigator.php?page=event_scheduling/CJ_invoice_information.php&uid=${this.userService.user.uid}n&option=R&debug_mode=2`,
                    '_blank'
                );
            }
        });
    }

    setLastDayTimestamp() {
        if (this.sub) {
            this.sub.unsubscribe();
        }
        if (this.date === null) {
            this.date = new Date();
        }
        this.date.setDate(this.date.getDate() - 1);
        this.getData();
    }

    async resetTimestamp() {
        this.date = null;
        await this.getData();
        this.sub = interval(10000).subscribe(async () => {
            await this.getData();
        });
    }

    ngOnDestroy(): void {
        if (this.sub) {
            this.sub.unsubscribe();
        }
    }
}
